import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, combineLatest, map, Observable, of, switchMap, tap } from 'rxjs';

import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import {
  CareServices,
  formatUsd,
  getInsuranceEstimate,
  getPlansForService,
  getSelfPayEstimate,
  IPlanOption,
  IStateData,
  ServiceValue
} from '@sondermind/cost-estimate-core';
import { CostEstimateStore } from '@sondermind/data-access/client-facing/cost-estimate';
import { DirectoryLinkService } from '@sondermind/data-access/directory-link';
import { ProviderNetworkCompanyStore } from '@sondermind/data-access/provider-network-company';
import { Logger } from '@sondermind/datadog-logging';
import { FilterSelectV2Component, IOptionCategory } from '@sondermind/features/filter-form';
import { IrisButtonModule, IrisButtonTypes } from '@sondermind/iris/button';
import { DropdownOption } from '@sondermind/iris/dropdown';
import { IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';
import { SegmentService } from '@sondermind/segment';
import {
  CASH_PAY_OPTION_VALUE,
  IInsurance,
  IInsuranceOptionValue,
  InsuranceCategoryName,
  InsuranceType
} from '@sondermind/utilities/models-provider-network-company';
import { IProviderShareableProfile } from '@sondermind/utilities/models-public-facing';

import { PSYCHIATRIST_EDUCATION_LEVELS } from '@sondermind/constants';

import { getValidStates } from '../../utils/valid-states.util';

const NO_STATE_TEXT = 'Select a state first';
const LOADING_TEXT = 'Loading…';
const SELECT_INSURANCE_TEXT = 'Select insurance';
const CASH_PAY_OPTION_NAME = 'I\'ll pay out of pocket';
/** Headings above the state and insurance dropdowns' options. */
const LICENSED_STATES_CATEGORY_NAME = 'This provider is licensed in these states:';
export const INSURANCE_CATEGORY_NAME = 'This provider accepts these coverage types:';

/** The surface these events are attributed to; the catalog admits only this value for the card. */
const COST_ESTIMATE_PROJECT = 'PSL';
/** `filter_value` for the self-pay option, which has no payer title of its own. */
const SELF_PAY_FILTER_VALUE = 'Self Pay';

/**
 * The catalog types these as string-literal unions, and a TS enum member is not assignable to one, so
 * both of these are `as const` objects rather than enums.
 */
const PAYMENT_TYPES = {
  insurance: 'insurance',
  selfPay: 'self_pay',
} as const;

/** Which of the card's dropdowns a `Cost Estimate Filter Selected` event describes. */
const FILTER_TYPES = {
  state: 'state',
  insurance: 'insurance',
} as const;

interface ICostEstimateForm {
  state: FormControl<string | null>;
  insurance: FormControl<IInsuranceOptionValue | null>;
}

/** The values the estimate panel renders. `insuranceTitle` goes unread on the self-pay variant. */
interface ICostEstimateVm {
  formattedCost: string;
  cost: number | string;
  isSelfPay: boolean;
  insuranceTitle: string;
  stateName: string;
  service: ServiceValue;
}

/** Everything one state's lookup yields: the plans the provider is in network with, and its pricing. */
interface IStatePlans {
  insurances: IInsurance[];
  stateData: IStateData | null;
}

/** What both the no-state and the failed-lookup paths resolve to: no plans, and nothing to price. */
const EMPTY_STATE_PLANS: IStatePlans = { insurances: [], stateData: null };

/**
 * Which set of copy the card renders. The number doubles as the footnote's superscript marker, and the
 * copy for each variant lives in the template.
 */
enum CostEstimateVariant {
  COMMON_COPAY = 1,
  INSURANCE_ESTIMATE = 2,
  SELF_PAY_ESTIMATE = 3,
}

/** Tags and names drift in casing and padding between the two backends, so compare them normalized. */
const toMatchKey = (value: string | null | undefined): string => (value ?? '').trim().toUpperCase();

@Component({
  selector: 'cost-estimate-psl',
  templateUrl: './cost-estimate-psl.component.html',
  styleUrls: ['./cost-estimate-psl.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, FilterSelectV2Component, IrisButtonModule, IrisIconComponent],
})
export class CostEstimatePslComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(Logger);
  private readonly providerNetworkCompanyStore = inject(ProviderNetworkCompanyStore);
  private readonly costEstimateStore = inject(CostEstimateStore);
  private readonly directoryLinkService = inject(DirectoryLinkService);
  private readonly segmentService = inject<SegmentService<SondermindEvents>>(SegmentService);

  readonly providerShareableProfile = input.required<IProviderShareableProfile>();

  /**
   * Booking is the page's to open — it needs the profile, address, share code, and native-app context
   * the parent holds — so this reports the click rather than handling it, the same way the header,
   * connect, and actions cards do.
   */
  readonly requestASessionClicked = output();

  protected readonly irisButtonTypes = IrisButtonTypes;
  protected readonly irisButtonTypeAttributes = IrisButtonTypeAttributeOptions;
  protected readonly costEstimateVariants = CostEstimateVariant;
  protected readonly iconSizes = IconSizes;

  protected readonly form = new FormGroup<ICostEstimateForm>({
    state: new FormControl<string | null>(null, Validators.required),
    insurance: new FormControl<IInsuranceOptionValue | null>(null, Validators.required),
  });

  protected readonly isPsychiatrist = computed(
    () => PSYCHIATRIST_EDUCATION_LEVELS.includes(this.providerShareableProfile().education_level)
  );

  /**
   * Every state the provider is licensed in — the same active-licensure intersection the
   * "Licensed in" block renders (MER-5032). Named for display, valued by abbreviation so the
   * selection lines up with `IProviderNetworkCompany.network_state`.
   */
  protected readonly stateOptions = computed<Array<IOptionCategory<string>>>(() => [{
    name: LICENSED_STATES_CATEGORY_NAME,
    options: getValidStates(this.providerShareableProfile())
      .map((state) => ({ name: state.name, value: state.abbrev }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  }]);

  private readonly selectedState = toSignal(this.form.controls.state.valueChanges);
  private readonly selectedInsurance = toSignal(this.form.controls.insurance.valueChanges);

  /** Cash pay carries its own disclaimer; every insurance-backed plan shares the other. */
  protected readonly variant = computed<CostEstimateVariant>(() => {
    const insurance = this.selectedInsurance();
    if (!insurance) return CostEstimateVariant.COMMON_COPAY;

    return insurance.type === InsuranceType.CASH
      ? CostEstimateVariant.SELF_PAY_ESTIMATE
      : CostEstimateVariant.INSURANCE_ESTIMATE;
  });

  /** The service this provider is booked for, which decides which of a plan's two rates applies. */
  private readonly service = computed<ServiceValue>(
    () => (this.isPsychiatrist() ? CareServices.Psychiatry.value : CareServices.Therapy.value)
  );

  private readonly stateInsurances = signal<IInsurance[]>([]);
  private readonly stateData = signal<IStateData | null>(null);
  private readonly isLoadingPlans = signal<boolean>(false);
  private readonly hasSubmitted = signal<boolean>(false);

  /**
   * True once the client has changed a filter with an estimate already on screen. Every event that
   * carries `is_revision` reads this, so "revision" means the same thing across all three.
   */
  private readonly hasRevisedEstimate = signal<boolean>(false);

  /**
   * The selected state's plans that apply to this provider's service. A plan that doesn't cover the
   * service is absent rather than priced, since `getInsuranceEstimate` hands back the raw rate without
   * checking whether the plan covers it.
   */
  private readonly servicePlans = computed<IPlanOption[]>(
    () => getPlansForService(this.stateData()?.plans ?? [], this.service())
  );

  /** Plans the provider accepts in the selected state, grouped the way the directory filters group them. */
  protected readonly insuranceOptions = computed<Array<IOptionCategory<IInsuranceOptionValue>>>(() => {
    const state = this.selectedState();

    return state ? this.toCategorizedOptions(this.stateInsurances(), state) : [];
  });

  protected readonly insuranceEmptyText = computed<string>(() => {
    if (!this.selectedState()) return NO_STATE_TEXT;

    return this.isLoadingPlans() ? LOADING_TEXT : SELECT_INSURANCE_TEXT;
  });

  /**
   * True once the client has asked a complete question. Both panel variants hang off this: the submit
   * button gives way to the auto-update hint, and the card grows a flat bottom edge to meet the panel.
   * A state change clears the plan, so an incomplete pair means the client owes us another answer.
   */
  protected readonly isEstimateRequested = computed<boolean>(
    () => this.hasSubmitted() && !!this.selectedState() && !!this.selectedInsurance()
  );

  /**
   * The figure to render, or null when there isn't one — which the template reads as the empty state,
   * since it only asks once `isEstimateRequested`. Everything it needs already arrived with the state,
   * so this is a plain derivation rather than a request: changing either dropdown re-prices
   * immediately, which is what the card promises once the panel is up.
   */
  protected readonly estimate = computed<ICostEstimateVm | null>(() => {
    const stateAbbrev = this.selectedState();
    const insurance = this.selectedInsurance();
    if (!this.hasSubmitted() || !stateAbbrev || !insurance) return null;

    const service = this.service();
    const isSelfPay = insurance.type === InsuranceType.CASH;
    const plan = isSelfPay ? null : this.pricingPlanFor(insurance, stateAbbrev);

    const { cost } = isSelfPay
      ? getSelfPayEstimate(this.stateData()?.selfPay, service)
      : getInsuranceEstimate(plan, service);

    if (!this.hasCost(cost)) {
      // `pricingPlanFor` already reported a failed match, so only say something when the row was found
      // (or is our own self-pay rate) and still has no figure for this service.
      if (isSelfPay || plan) {
        this.logger.warn('Cost estimate found no rate to quote for the selection', {
          state: stateAbbrev,
          service,
          isSelfPay,
          planTag: plan?.tag,
          planName: plan?.name
        });
      }

      return null;
    }

    return {
      formattedCost: formatUsd(cost),
      cost,
      isSelfPay,
      insuranceTitle: insurance.title,
      stateName: this.stateNameFor(stateAbbrev),
      service
    };
  });

  /**
   * The directory, pre-filtered by what the client just answered, so "other providers" means others who
   * fit the same estimate. It's a separate app at its own origin, so this is an absolute href rather
   * than a `routerLink`. Cash pay isn't one of the directory's insurance filters, so it narrows by state
   * alone.
   */
  protected readonly browseOtherProvidersUrl = computed<string>(() => {
    const insurance = this.selectedInsurance();
    const insuranceTitle = insurance && insurance.type !== InsuranceType.CASH
      ? insurance.title
      : undefined;

    return this.directoryLinkService.buildProviderDirectoryLink(
      this.selectedState() ?? undefined,
      undefined,
      insuranceTitle
    );
  });

  constructor() {
    // `Cost Estimate Shown` reports an outcome, not an interaction, so it hangs off the rendered result
    // rather than the submit or the dropdowns: one event per panel the client actually sees, including
    // the unavailable one. An effect is the right tool here — this is a side effect of a rendered
    // state, not state propagation. Both branches read the same signals, so the effect re-runs on every
    // re-price rather than only the first.
    effect(() => {
      if (!this.isEstimateRequested()) return;

      const estimate = this.estimate();
      const insurance = this.selectedInsurance();
      const state = this.selectedState();
      // `isEstimateRequested` already guarantees both, but the compiler doesn't know that.
      if (!insurance || !state) return;

      const isSelfPay = insurance.type === InsuranceType.CASH;

      this.segmentService.track<SondermindEvent.CostEstimateShown>(
        SondermindEvent.CostEstimateShown,
        {
          project: COST_ESTIMATE_PROJECT,
          personaProviderId: this.providerShareableProfile().id,
          estimateAvailable: !!estimate,
          costEstimate: this.toNumericCost(estimate?.cost),
          paymentType: isSelfPay ? PAYMENT_TYPES.selfPay : PAYMENT_TYPES.insurance,
          payerName: isSelfPay ? null : insurance.title,
          state,
          service: this.service(),
          isRevision: this.hasRevisedEstimate(),
        }
      ).catch(() => { });
    });
  }

  ngOnInit(): void {
    this.loadPlansOnStateChange();
    this.trackFilterSelections();
  }

  /**
   * Refills the insurance dropdown and its pricing whenever the state changes. `switchMap` drops an
   * in-flight lookup when the client picks again, so a slow response can't land against a newer state.
   */
  private loadPlansOnStateChange(): void {
    this.form.controls.state.valueChanges.pipe(
      tap(() => {
        // A plan is only valid within the state it was listed under, so switching state drops it.
        this.form.controls.insurance.setValue(null);
        this.stateInsurances.set([]);
        this.stateData.set(null);
        this.isLoadingPlans.set(!!this.form.controls.state.value);
      }),
      switchMap((state) => (state ? this.getStatePlans$(state) : of<IStatePlans>(EMPTY_STATE_PLANS))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ insurances, stateData }) => {
      const state = this.form.controls.state.value;
      // Pricing that arrives empty isn't an error, but it silently strips every figure from the state.
      if (state && stateData && !stateData.plans.length) {
        this.logger.warn('Cost estimate pricing carried no plans for the state', { state });
      }

      this.stateInsurances.set(insurances);
      this.stateData.set(stateData);
      this.isLoadingPlans.set(false);
    });
  }

  /**
   * One state's lookup: the plans the provider is in network with, and the pricing for them. The two
   * come from different backends — one fills the dropdown, the other prices whatever gets picked — so
   * pricing degrades on its own and an outage costs the client the figure, not the whole question.
   */
  private getStatePlans$(state: string): Observable<IStatePlans> {
    return combineLatest([
      this.providerNetworkCompanyStore.getInsuranceOptionsByState$({ state }),
      this.costEstimateStore.getStateData$(state).pipe(
        catchError((err: unknown) => {
          this.logger.error('Failed to load cost-estimate pricing', { err });
          return of<IStateData | null>(null);
        })
      )
    ]).pipe(
      map(([insurances, stateData]): IStatePlans => ({ insurances, stateData })),
      catchError((err: unknown) => {
        this.logger.error('Failed to load cost-estimate insurance options', { err });
        return of<IStatePlans>(EMPTY_STATE_PLANS);
      })
    );
  }

  /**
   * Opens the estimate. Only the first submit matters — from then on `estimate` tracks the dropdowns
   * directly, so the button is replaced by the auto-update hint.
   */
  protected onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.hasSubmitted.set(true);
  }

  /**
   * Reports the click and hands the booking flow to the parent. The figure is read from the panel the
   * client was looking at, so the event describes what was on screen rather than a fresh derivation.
   */
  protected onScheduleSessionClicked(): void {
    const estimate = this.estimate();
    const insurance = this.selectedInsurance();

    this.requestASessionClicked.emit();

    // The button only renders inside the panel, so both are present; guard for the compiler's sake.
    if (!estimate || !insurance) return;

    this.segmentService.track<SondermindEvent.CostEstimateScheduleSessionClicked>(
      SondermindEvent.CostEstimateScheduleSessionClicked,
      {
        project: COST_ESTIMATE_PROJECT,
        personaProviderId: this.providerShareableProfile().id,
        costEstimate: this.toNumericCost(estimate.cost),
        paymentType: estimate.isSelfPay ? PAYMENT_TYPES.selfPay : PAYMENT_TYPES.insurance,
        payerName: estimate.isSelfPay ? null : estimate.insuranceTitle,
        state: this.form.controls.state.value ?? '',
        isRevision: this.hasRevisedEstimate(),
      }
    ).catch(() => { });
  }

  /**
   * One event per dropdown change. Both streams feed the same event with a different `filterType`, and
   * a change made while an estimate is up is a revision — which also arms `is_revision` for the
   * re-priced `Cost Estimate Shown` that follows, since the flag is read after this runs.
   */
  private trackFilterSelections(): void {
    this.form.controls.state.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((state) => {
      if (!state) return;

      this.markRevisionIfEstimateShown();
      this.trackFilterSelected(FILTER_TYPES.state, state);
    });

    this.form.controls.insurance.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((insurance) => {
      // Clearing the insurance is a consequence of a state change, not a selection of its own.
      if (!insurance) return;

      this.markRevisionIfEstimateShown();
      this.trackFilterSelected(
        FILTER_TYPES.insurance,
        insurance.type === InsuranceType.CASH ? SELF_PAY_FILTER_VALUE : insurance.title
      );
    });
  }

  private trackFilterSelected(
    filterType: typeof FILTER_TYPES[keyof typeof FILTER_TYPES],
    filterValue: string
  ): void {
    this.segmentService.track<SondermindEvent.CostEstimateFilterSelected>(
      SondermindEvent.CostEstimateFilterSelected,
      {
        project: COST_ESTIMATE_PROJECT,
        personaProviderId: this.providerShareableProfile().id,
        filterType,
        filterValue,
        isRevision: this.hasRevisedEstimate(),
      }
    ).catch(() => { });
  }

  /** A filter change only counts as a revision once the panel is up; before that it's the first ask. */
  private markRevisionIfEstimateShown(): void {
    if (this.hasSubmitted()) this.hasRevisedEstimate.set(true);
  }

  /**
   * The catalog types `cost_estimate` as a nullable number, but a few plans carry a range string like
   * "25-40" that has no single numeric value. Those report null rather than a parsed endpoint, which
   * would misstate the figure the client saw.
   */
  private toNumericCost(cost: number | string | null | undefined): number | null {
    return typeof cost === 'number' ? cost : null;
  }

  /**
   * The pricing row behind a network plan, matched on tag — the only key the two backends share, since
   * they number their rows independently and the pricing ids only line up against prod. A few tags are
   * carried by more than one plan, so the display name settles which rate applies; when nothing settles
   * it we quote nothing rather than pick one, because a wrong figure is worse than none. Null renders as
   * the empty state: the profile still lists the plan as accepted, we just can't price it.
   *
   * Every way this returns null is logged, since each one is a disagreement between the two backends
   * that costs a client their estimate and can only be diagnosed from the tag that missed.
   */
  private pricingPlanFor(insurance: IInsuranceOptionValue, state: string): IPlanOption | null {
    const tag = toMatchKey(insurance.tag);
    // Without a tag on both sides there is nothing to match on, and pairing by anything else is a guess.
    if (!tag) {
      this.logger.warn('Cost estimate cannot price an insurance option that carries no tag', {
        state,
        pncId: insurance.pncId,
        planName: insurance.title
      });

      return null;
    }

    const matches = this.servicePlans().filter((plan) => toMatchKey(plan.tag) === tag);
    if (!matches.length) {
      this.logger.warn('Cost estimate has no pricing plan for the selected insurance tag', {
        state,
        tag,
        pncId: insurance.pncId,
        planName: insurance.title
      });

      return null;
    }

    if (matches.length === 1) return matches[0];

    const named = matches.find((plan) => toMatchKey(plan.name) === toMatchKey(insurance.title));
    if (!named) {
      this.logger.error('Cost estimate cannot tell duplicate pricing tags apart by plan name', {
        state,
        tag,
        planName: insurance.title,
        candidateNames: matches.map((plan) => plan.name)
      });
    }

    return named ?? null;
  }

  /** A rate we can actually put on screen. Core leaves `cost` unset when it has nothing to quote. */
  private hasCost(cost: number | string | null | undefined): cost is number | string {
    return cost !== null && cost !== undefined && cost !== '';
  }

  /** The disclaimer names the state, so fall back to the abbreviation rather than an empty phrase. */
  private stateNameFor(abbrev: string): string {
    const [states] = this.stateOptions();

    return states.options.find((option) => option.value === abbrev)?.name ?? abbrev;
  }

  /**
   * Splits the state's plans into the same categories the directory's filter dropdown uses, keeping
   * only the ones this provider is in network with. Cash pay leads the list in an unnamed group, so
   * it reads as the default rather than as one more plan.
   */
  private toCategorizedOptions(
    insurances: IInsurance[],
    state: string
  ): Array<IOptionCategory<IInsuranceOptionValue>> {
    const acceptedPncIds = new Set(
      (this.providerShareableProfile().provider_network_companies ?? [])
        .filter((pnc) => pnc.network_state === state)
        .map((pnc) => pnc.id)
    );
    const cashPay: IOptionCategory<IInsuranceOptionValue> = {
      name: INSURANCE_CATEGORY_NAME,
      options: [{ name: CASH_PAY_OPTION_NAME, value: CASH_PAY_OPTION_VALUE }]
    };
    const medicare: IOptionCategory<IInsuranceOptionValue> = {
      name: InsuranceCategoryName.MEDICARE,
      options: []
    };
    const medicaid: IOptionCategory<IInsuranceOptionValue> = {
      name: InsuranceCategoryName.MEDICAID,
      options: []
    };
    const insurance: IOptionCategory<IInsuranceOptionValue> = {
      name: InsuranceCategoryName.INSURANCE,
      options: []
    };
    const eap: IOptionCategory<IInsuranceOptionValue> = {
      name: InsuranceCategoryName.EAP,
      options: []
    };

    insurances
      // Every plan the provider accepts is offered, even one we can't price — the profile already lists
      // it as accepted, so hiding it here would read as a contradiction. An unpriceable pick answers
      // with the empty state instead.
      .filter((ins) => acceptedPncIds.has(ins.pncId))
      .forEach((ins) => {
        // One plan may belong to several categories.
        if (ins.isMedicare) medicare.options.push(this.toOption(ins, InsuranceType.MEDICARE));
        if (ins.isMedicaid) medicaid.options.push(this.toOption(ins, InsuranceType.MEDICAID));
        if (ins.isPrivateInsurance) insurance.options.push(this.toOption(ins, InsuranceType.INSURANCE));
        if (ins.isEap) eap.options.push(this.toOption(ins, InsuranceType.EAP));
      });

    return [cashPay, medicare, medicaid, insurance, eap];
  }

  private toOption(insurance: IInsurance, type: InsuranceType): DropdownOption<IInsuranceOptionValue> {
    return {
      name: insurance.title,
      value: {
        pncId: insurance.pncId,
        title: insurance.title,
        type,
        tag: insurance.tag
      }
    };
  }
}
